// Modern Light Admin Blog + Payment Gateway UI

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Save,
  FileText,
  CreditCard,
  Eye,
  EyeOff,
  ExternalLink,
  Search,
  Sparkles,
  ShieldCheck,
  Building2,
} from "lucide-react";

import { API, getToken } from "../../lib/auth";
import { useConfirm } from "../../components/ConfirmDialog";

const cfg = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
});

const cls = {
  card: "bg-white border border-slate-200 rounded-[28px] shadow-sm",
  input:
    "w-full h-12 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none transition focus:border-emerald-500 focus:bg-white",
  textarea:
    "w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:bg-white",
  label:
    "block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2",
  primary:
    "inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-green-500 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:scale-[1.01]",
};

// ────────────────────────── BLOG ──────────────────────────

export function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});

  const confirm = useConfirm();

  const load = () =>
    axios
      .get(`${API}/admin/blog`, cfg())
      .then((r) => setPosts(r.data || []));

  useEffect(() => {
    load();
  }, []);

  const slugify = (s) =>
    (s || "")
      .toLowerCase()
      .trim()
      .replace(/[^\w]+/g, "-")
      .replace(/^-|-$/g, "");

  const openCreate = () => {
    setForm({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      tags: [],
      author: "Brivo Team",
      published: false,
      cover_url: "",
    });

    setEdit(null);
    setOpen(true);
  };

  const openEdit = (p) => {
    setForm({ ...p, tags: p.tags || [] });
    setEdit(p);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.title || !form.content) {
      return toast.error("Title and content are required");
    }

    if (
      !(await confirm({
        title: edit ? "Save blog changes?" : "Publish new post?",
        message: form.published
          ? "This article will be visible publicly."
          : "Post will remain as draft.",
      }))
    )
      return;

    try {
      const slug = form.slug || slugify(form.title);

      const payload = {
        ...form,
        slug,
      };

      if (edit) {
        await axios.patch(
          `${API}/admin/blog/${edit.id}`,
          payload,
          cfg()
        );
      } else {
        await axios.post(`${API}/admin/blog`, payload, cfg());
      }

      toast.success("Post saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (p) => {
    if (
      !(await confirm({
        title: "Delete post?",
        message: `"${p.title}" will be removed permanently.`,
        tone: "danger",
        yesLabel: "Delete",
      }))
    )
      return;

    await axios.delete(`${API}/admin/blog/${p.id}`, cfg());

    toast.success("Deleted");
    load();
  };

  const filtered = posts.filter((p) =>
    [p.title, p.slug, p.author]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F7F9FC] p-6 lg:p-10">
      <div className="mx-auto max-w-[1600px]">

        {/* Header */}
        <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">

          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
              <Sparkles size={15} />
              Content Management
            </div>

            <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-900">
              Blog Management
            </h1>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Manage marketing blog posts, drafts, announcements, and SEO
              content for your Brivo platform.
            </p>
          </div>

          <button
            onClick={openCreate}
            className={cls.primary}
          >
            <Plus size={16} />
            New Post
          </button>
        </div>

        {/* Search */}
        <div className="mb-6 flex items-center gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">

          <Search size={18} className="text-slate-400" />

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search blog posts..."
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>

        {/* Table */}
        <div className={`${cls.card} overflow-hidden`}>

          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                All Blog Posts
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                {filtered.length} posts found
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">

              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Title
                  </th>

                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Slug
                  </th>

                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Author
                  </th>

                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Status
                  </th>

                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Published
                  </th>

                  <th className="px-6 py-4 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    className="transition hover:bg-slate-50"
                  >
                    <td className="px-6 py-5">
                      <div className="font-semibold text-slate-900">
                        {p.title}
                      </div>
                    </td>

                    <td className="px-6 py-5 font-mono text-xs text-slate-500">
                      {p.slug}
                    </td>

                    <td className="px-6 py-5 text-sm text-slate-700">
                      {p.author}
                    </td>

                    <td className="px-6 py-5">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-[11px] font-bold ${
                          p.published
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-slate-100 text-slate-600 border border-slate-200"
                        }`}
                      >
                        {p.published ? "Live" : "Draft"}
                      </span>
                    </td>

                    <td className="px-6 py-5 text-sm text-slate-500">
                      {(p.published_at || "").slice(0, 10) || "—"}
                    </td>

                    <td className="px-6 py-5">
                      <div className="flex justify-end gap-2">

                        <a
                          href={`/blog/${p.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 transition hover:bg-slate-200"
                        >
                          <ExternalLink size={15} />
                        </a>

                        <button
                          onClick={() => openEdit(p)}
                          className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition hover:bg-blue-100"
                        >
                          <Pencil size={15} />
                        </button>

                        <button
                          onClick={() => del(p)}
                          className="flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                        >
                          <Trash2 size={15} />
                        </button>

                      </div>
                    </td>
                  </tr>
                ))}

                {!filtered.length && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-20 text-center"
                    >
                      <FileText
                        size={34}
                        className="mx-auto text-slate-300"
                      />

                      <h3 className="mt-4 text-lg font-bold text-slate-900">
                        No blog posts yet
                      </h3>

                      <p className="mt-2 text-sm text-slate-500">
                        Start creating articles for your platform.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>

            </table>
          </div>
        </div>

        {/* Modal */}
        {open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">

            <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-[32px] bg-white shadow-2xl">

              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">

                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <FileText size={20} />
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {edit ? "Edit Post" : "Create New Post"}
                    </h3>

                    <p className="text-sm text-slate-500">
                      Manage your blog article content
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setOpen(false)}
                  className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-600"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-5 overflow-y-auto p-6">

                <div className="col-span-2">
                  <label className={cls.label}>Title</label>

                  <input
                    className={cls.input}
                    value={form.title || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        title: e.target.value,
                        slug:
                          form.slug ||
                          slugify(e.target.value),
                      })
                    }
                  />
                </div>

                <div>
                  <label className={cls.label}>Slug</label>

                  <input
                    className={cls.input}
                    value={form.slug || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        slug: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className={cls.label}>Author</label>

                  <input
                    className={cls.input}
                    value={form.author || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        author: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-span-2">
                  <label className={cls.label}>
                    Cover Image URL
                  </label>

                  <input
                    className={cls.input}
                    value={form.cover_url || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        cover_url: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-span-2">
                  <label className={cls.label}>Excerpt</label>

                  <textarea
                    className={`${cls.textarea} min-h-[100px]`}
                    value={form.excerpt || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        excerpt: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-span-2">
                  <label className={cls.label}>Content</label>

                  <textarea
                    className={`${cls.textarea} min-h-[260px] font-mono text-xs`}
                    value={form.content || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        content: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-span-2">
                  <label className={cls.label}>Tags</label>

                  <input
                    className={cls.input}
                    value={(form.tags || []).join(", ")}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        tags: e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!form.published}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          published: e.target.checked,
                        })
                      }
                    />

                    Publish immediately
                  </label>
                </div>

              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-5">

                <button
                  onClick={() => setOpen(false)}
                  className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-700"
                >
                  Cancel
                </button>

                <button
                  onClick={submit}
                  className={cls.primary}
                >
                  <Save size={15} />
                  Save Post
                </button>

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────── PAYMENT GATEWAY ──────────────────────────

export function AdminPaymentGateway() {
  const [s, setS] = useState(null);
  const [show, setShow] = useState(false);
  const [secret, setSecret] = useState("");

  const confirm = useConfirm();

  const load = () =>
    axios
      .get(`${API}/admin/gateway-settings`, cfg())
      .then((r) => setS(r.data));

  useEffect(() => {
    load();
  }, []);

  if (!s) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] p-10">
        Loading...
      </div>
    );
  }

  const save = async () => {
    if (
      !(await confirm({
        title: "Save gateway settings?",
        message:
          "These payment settings will apply platform-wide immediately.",
      }))
    )
      return;

    try {
      const payload = { ...s };

      if (secret) {
        payload.paystack_secret_key = secret;
      }

      delete payload.paystack_secret_key_masked;
      delete payload.id;

      await axios.patch(
        `${API}/admin/gateway-settings`,
        payload,
        cfg()
      );

      toast.success("Settings updated");

      setSecret("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] p-6 lg:p-10">
      <div className="mx-auto max-w-[1600px]">

        {/* Header */}
        <div className="mb-8">

          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
            <ShieldCheck size={15} />
            Secure Payments
          </div>

          <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-900">
            Payment Gateway Settings
          </h1>

          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Configure payment providers, bank transfer details, and secure
            checkout options for all tenants.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">

          {/* Paystack */}
          <div className={`${cls.card} p-7`}>

            <div className="mb-6 flex items-center gap-4">

              <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-emerald-50 text-emerald-600">
                <CreditCard size={24} />
              </div>

              <div>
                <h3 className="text-xl font-black text-slate-900">
                  Paystack
                </h3>

                <p className="text-sm text-slate-500">
                  Configure Paystack card payments
                </p>
              </div>

            </div>

            <div className="space-y-5">

              <div>
                <label className={cls.label}>Public Key</label>

                <input
                  className={cls.input}
                  value={s.paystack_public_key || ""}
                  onChange={(e) =>
                    setS({
                      ...s,
                      paystack_public_key: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className={cls.label}>
                  Secret Key
                </label>

                <div className="relative">

                  <input
                    type={show ? "text" : "password"}
                    className={`${cls.input} pr-12`}
                    value={secret}
                    onChange={(e) =>
                      setSecret(e.target.value)
                    }
                    placeholder={
                      s.paystack_secret_key_masked
                        ? "Leave blank to keep existing"
                        : "sk_test_..."
                    }
                  />

                  <button
                    onClick={() => setShow(!show)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500"
                  >
                    {show ? (
                      <EyeOff size={17} />
                    ) : (
                      <Eye size={17} />
                    )}
                  </button>

                </div>
              </div>

              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={!!s.paystack_enabled}
                  onChange={(e) =>
                    setS({
                      ...s,
                      paystack_enabled: e.target.checked,
                    })
                  }
                />

                Enable Paystack
              </label>

            </div>
          </div>

          {/* Bank */}
          <div className={`${cls.card} p-7`}>

            <div className="mb-6 flex items-center gap-4">

              <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-amber-50 text-amber-600">
                <Building2 size={24} />
              </div>

              <div>
                <h3 className="text-xl font-black text-slate-900">
                  Bank Transfer
                </h3>

                <p className="text-sm text-slate-500">
                  Manual transfer payment settings
                </p>
              </div>

            </div>

            <div className="space-y-5">

              <div>
                <label className={cls.label}>Bank Name</label>

                <input
                  className={cls.input}
                  value={s.bank_name || ""}
                  onChange={(e) =>
                    setS({
                      ...s,
                      bank_name: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className={cls.label}>
                  Account Holder
                </label>

                <input
                  className={cls.input}
                  value={s.bank_account_holder || ""}
                  onChange={(e) =>
                    setS({
                      ...s,
                      bank_account_holder: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className={cls.label}>
                  Account Number
                </label>

                <input
                  className={cls.input}
                  value={s.bank_account_number || ""}
                  onChange={(e) =>
                    setS({
                      ...s,
                      bank_account_number: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className={cls.label}>
                  Payment Instructions
                </label>

                <textarea
                  className={`${cls.textarea} min-h-[120px]`}
                  value={s.bank_instructions || ""}
                  onChange={(e) =>
                    setS({
                      ...s,
                      bank_instructions: e.target.value,
                    })
                  }
                />
              </div>

            </div>
          </div>

        </div>

        <div className="mt-8 flex justify-end">

          <button
            onClick={save}
            className={cls.primary}
          >
            <Save size={15} />
            Save Settings
          </button>

        </div>
      </div>
    </div>
  );
}